/**
 * Shared profile building blocks:
 *   - AddressFields      (line1/line2/city/state/country/postal_code)
 *   - PhoneField         (dial-code picker + number)
 *   - CountrySelect      (reusable dropdown)
 *   - IdProofField       (URL input + preview)
 *
 * All three read the country catalog from `/api/countries` once per session
 * and cache it on the window so the list is always instantly available.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { ChevronDown, Check, Search, Upload, Image as ImageIcon } from "lucide-react";

// ---------- tiny global cache ---------------------------------------------
let _countriesPromise = null;
function loadCountries() {
  if (!_countriesPromise) {
    _countriesPromise = api.get("/countries").then(({ data }) => data).catch(() => []);
  }
  return _countriesPromise;
}
export function useCountries() {
  const [rows, setRows] = useState([]);
  useEffect(() => { loadCountries().then(setRows); }, []);
  return rows;
}

// Profile-field policy cache, keyed by role.
// Returns `{ fields: {name:"required", tax_id:"hidden", ...} }` with
// everything defaulted to "optional" if no doc exists.
const _policyPromises = {};
function loadPolicy(role) {
  if (!_policyPromises[role]) {
    _policyPromises[role] = api.get(`/profile-field-policy/${role}`)
      .then(({ data }) => data).catch(() => ({ fields: {} }));
  }
  return _policyPromises[role];
}
export function useFieldPolicy(role) {
  const [state, setState] = useState({ fields: {} });
  useEffect(() => { loadPolicy(role).then(setState); }, [role]);
  return state;
}
// Tiny util for components with a simple "required + hidden" display.
export function policyFor(policy, field) {
  const s = policy?.fields?.[field] || "optional";
  return { state: s, hidden: s === "hidden", required: s === "required" };
}

// ---------- CountrySelect --------------------------------------------------
export function CountrySelect({ value, onChange, testid = "country-select", disabled, className = "" }) {
  const countries = useCountries();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const rootRef = useRef(null);
  const selected = countries.find(c => c.iso2 === (value || "").toUpperCase());

  useEffect(() => {
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const rows = s ? countries.filter(c =>
      c.name.toLowerCase().includes(s) || c.iso2.toLowerCase() === s || c.dial_code.includes(s)
    ) : countries;
    return rows.slice(0, 120);
  }, [q, countries]);

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button type="button" disabled={disabled}
        onClick={() => setOpen(o => !o)}
        data-testid={testid}
        className={`input-dark w-full flex items-center justify-between gap-2 ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}>
        <span className="truncate flex items-center gap-2">
          {selected ? (
            <>
              <span className="text-lg leading-none">{selected.flag}</span>
              <span className="truncate">{selected.name}</span>
              <span className="text-[#A1A1AA] text-xs">({selected.dial_code})</span>
            </>
          ) : (
            <span className="text-[#A1A1AA]">Select country…</span>
          )}
        </span>
        <ChevronDown size={14} className="text-[#A1A1AA] shrink-0"/>
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-20 rounded-xl bg-[#0F0817] border border-[#3D2845] shadow-2xl overflow-hidden"
             data-testid={`${testid}-menu`}>
          <div className="p-2 border-b border-[#3D2845] flex items-center gap-2">
            <Search size={14} className="text-[#A1A1AA]"/>
            <input autoFocus className="bg-transparent outline-none text-sm w-full placeholder:text-[#A1A1AA]"
              placeholder="Search country or dial code…"
              value={q} onChange={(e) => setQ(e.target.value)}
              data-testid={`${testid}-search`}/>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {filtered.map(c => (
              <button key={c.iso2} type="button"
                onClick={() => { onChange(c.iso2); setOpen(false); setQ(""); }}
                data-testid={`${testid}-opt-${c.iso2}`}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-[#261534] transition ${
                  selected?.iso2 === c.iso2 ? "bg-[#D4FF00]/10 text-[#D4FF00]" : "text-[#FAFAFA]"
                }`}>
                <span className="text-lg leading-none">{c.flag}</span>
                <span className="flex-1 truncate text-left">{c.name}</span>
                <span className="text-[11px] text-[#A1A1AA]">{c.dial_code}</span>
                {selected?.iso2 === c.iso2 && <Check size={14}/>}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-[#A1A1AA]">No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- PhoneField -----------------------------------------------------
export function PhoneField({ phone, phoneCountry, onChange, editable = true, testid = "phone-field", required }) {
  const countries = useCountries();
  const active = countries.find(c => c.iso2 === (phoneCountry || "").toUpperCase());
  return (
    <div>
      <Label testid={`${testid}-label`} required={required}>Phone number</Label>
      <div className="grid grid-cols-[auto_1fr] gap-2">
        <div className="min-w-[140px]">
          <CountrySelect value={phoneCountry || ""} disabled={!editable}
            testid={`${testid}-cc`}
            onChange={(iso2) => {
              const c = countries.find(x => x.iso2 === iso2);
              onChange({ phone_country: iso2, phone_cc: c?.dial_code || "" });
            }}/>
        </div>
        <input type="tel" disabled={!editable}
          className="input-dark"
          value={phone || ""}
          data-testid={`${testid}-number`}
          onChange={(e) => onChange({ phone: e.target.value })}
          placeholder={active ? `e.g. ${active.dial_code} 555 123 4567` : "Phone number"}/>
      </div>
    </div>
  );
}

// ---------- AddressFields --------------------------------------------------
// Country/State/City are now dependent dropdowns powered by the
// `country-state-city` package (offline catalogue, ~245 countries with
// states + cities). When the user picks a country, the state list
// re-populates; when they pick a state, the city list does too.
//
// Falls back to a plain text input if the catalog has no entries for a
// given country (e.g. small island nations) — matches user intent
// rather than blocking input.
import { State, City } from "country-state-city";

export function AddressFields({ value, onChange, editable = true, testid = "address", requiredFields = [] }) {
  const v = value || {};
  const set = (k) => (val) => onChange({ ...v, [k]: val });
  const setE = (k) => (e) => onChange({ ...v, [k]: e.target.value });
  const req = (k) => requiredFields.includes(k);
  const cc = (v.country || "").toUpperCase();
  const states = cc ? State.getStatesOfCountry(cc) : [];
  const stateCode = states.find(s => s.name === v.state)?.isoCode;
  const cities = cc && stateCode ? City.getCitiesOfState(cc, stateCode) : [];
  const showStateDropdown = states.length > 0;
  const showCityDropdown = cities.length > 0;

  // When the user switches country, blow away stale state/city values.
  const onCountry = (newCC) => {
    onChange({ ...v, country: newCC, state: "", city: "" });
  };
  const onState = (newName) => {
    onChange({ ...v, state: newName, city: "" });
  };
  return (
    <div className="grid gap-3">
      {/* Country first — required to drive the state/city dropdowns. */}
      <div>
        <Label required={req("address_country")}>Country</Label>
        {editable ? (
          <CountrySelect value={v.country || ""} onChange={onCountry} testid={`${testid}-country`}/>
        ) : (
          <div className="input-dark flex items-center gap-2 opacity-80">
            {v.country ? <ReadonlyCountry iso2={v.country}/> : <span className="text-[#A1A1AA]">—</span>}
          </div>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {showStateDropdown ? (
          <div>
            <Label required={req("address_state")}>State / Region</Label>
            <select className="input-dark" value={v.state || ""}
              onChange={e => onState(e.target.value)}
              data-testid={`${testid}-state`}
              disabled={!editable}>
              <option value="">{cc ? "Select state…" : "Pick a country first"}</option>
              {states.map(s => (
                <option key={s.isoCode} value={s.name}>{s.name}</option>
              ))}
            </select>
          </div>
        ) : (
          <Input label="State / Region" value={v.state || ""} required={req("address_state")}
            onChange={setE("state")} editable={editable} testid={`${testid}-state`}/>
        )}
        {showCityDropdown ? (
          <div>
            <Label required={req("address_city")}>City</Label>
            <select className="input-dark" value={v.city || ""}
              onChange={setE("city")}
              data-testid={`${testid}-city`}
              disabled={!editable}>
              <option value="">{stateCode ? "Select city…" : "Pick a state first"}</option>
              {cities.map(ct => (
                <option key={`${ct.name}-${ct.latitude}`} value={ct.name}>{ct.name}</option>
              ))}
            </select>
          </div>
        ) : (
          <Input label="City" value={v.city || ""} required={req("address_city")}
            onChange={setE("city")} editable={editable} testid={`${testid}-city`}/>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input label="Address line 1" value={v.line1 || ""} required={req("address_line1")}
          onChange={setE("line1")} editable={editable} testid={`${testid}-line1`}/>
        <Input label="Address line 2" value={v.line2 || ""} required={req("address_line2")}
          onChange={setE("line2")} editable={editable} testid={`${testid}-line2`}/>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Input label="Postal / ZIP" value={v.postal_code || ""} required={req("address_postal_code")}
          onChange={setE("postal_code")} editable={editable} testid={`${testid}-postal`}/>
      </div>
    </div>
  );
}

function ReadonlyCountry({ iso2 }) {
  const countries = useCountries();
  const c = countries.find(x => x.iso2 === (iso2 || "").toUpperCase());
  if (!c) return <span>{iso2}</span>;
  return <span className="flex items-center gap-2">
    <span className="text-lg">{c.flag}</span>
    <span>{c.name}</span>
  </span>;
}

// ---------- IdProofField ---------------------------------------------------
export function IdProofField({ value, onChange, editable = true, testid = "id-proof", required }) {
  const fileRef = useRef(null);
  const pickFile = () => fileRef.current?.click();
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Read as data URL — keeps this feature lightweight (<2 MB). Server-side
    // storage to S3/similar can be swapped in without UI changes.
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(f);
  };
  return (
    <div>
      <Label required={required}>ID proof</Label>
      <div className="grid gap-2">
        {value ? (
          <div className="flex items-center gap-3">
            <img src={value} alt="ID proof preview" className="w-20 h-20 rounded-xl object-cover border border-[#3D2845]"/>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-[#A1A1AA] truncate">Uploaded</div>
            </div>
            {editable && (
              <button onClick={() => onChange("")} type="button"
                data-testid={`${testid}-clear`}
                className="text-xs text-[#FF007F] hover:underline">Remove</button>
            )}
          </div>
        ) : (
          editable ? (
            <button type="button" onClick={pickFile} data-testid={`${testid}-upload`}
              className="border border-dashed border-[#3D2845] hover:border-[#D4FF00] transition rounded-xl p-4 text-center text-sm text-[#A1A1AA] flex flex-col items-center gap-2">
              <ImageIcon size={18}/>
              <div>Click to upload a passport / driving licence / aadhaar image</div>
              <div className="text-[10px] text-[#A1A1AA]">Stays encrypted on our servers. Shown only to admins.</div>
            </button>
          ) : <div className="text-sm text-[#A1A1AA]">Not uploaded</div>
        )}
        {editable && (
          <>
            <input ref={fileRef} type="file" accept="image/*" className="hidden"
              onChange={onFile} data-testid={`${testid}-input`}/>
            <details className="text-[11px] text-[#A1A1AA]">
              <summary className="cursor-pointer">…or paste an image URL instead</summary>
              <input className="input-dark mt-2" placeholder="https://…" value={(value||"").startsWith("data:") ? "" : (value || "")}
                data-testid={`${testid}-url`}
                onChange={(e) => onChange(e.target.value)}/>
            </details>
          </>
        )}
      </div>
    </div>
  );
}

// ---------- CountryMultiSelect -------------------------------------------
// Seller shipping-countries picker. Supports the special "ALL" sentinel
// (renders as a single pill "🌍 Worldwide") and a searchable list. The
// user can toggle between worldwide and a curated list with one click.
export function CountryMultiSelect({ value, onChange, testid = "country-multi" }) {
  const countries = useCountries();
  const [q, setQ] = useState("");
  const selectedSet = useMemo(() => new Set(value || []), [value]);
  const worldwide = selectedSet.has("ALL");

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const rows = s ? countries.filter(c =>
      c.name.toLowerCase().includes(s) || c.iso2.toLowerCase() === s
    ) : countries;
    return rows;
  }, [q, countries]);

  const toggle = (iso2) => {
    if (worldwide) return; // noop while "Worldwide" is on
    const next = new Set(selectedSet);
    if (next.has(iso2)) next.delete(iso2); else next.add(iso2);
    onChange([...next].filter(x => x !== "ALL"));
  };

  return (
    <div className="grid gap-2" data-testid={testid}>
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => onChange(worldwide ? [] : ["ALL"])}
          data-testid={`${testid}-worldwide`}
          className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition ${
            worldwide
              ? "bg-[#D4FF00] text-black shadow-[0_6px_20px_rgba(212,255,0,0.35)]"
              : "bg-[#261534] text-[#A1A1AA] border border-[#3D2845] hover:border-[#D4FF00]"
          }`}>
          🌍 Worldwide
        </button>
        {!worldwide && (
          <div className="text-xs text-[#A1A1AA]">
            {selectedSet.size > 0
              ? `Ships to ${selectedSet.size} ${selectedSet.size === 1 ? "country" : "countries"}`
              : "Pick the countries you ship to"}
          </div>
        )}
      </div>

      {!worldwide && (
        <>
          <div className="rounded-xl bg-[#0F0817] border border-[#3D2845]">
            <div className="p-2 border-b border-[#3D2845] flex items-center gap-2">
              <Search size={14} className="text-[#A1A1AA]"/>
              <input
                className="bg-transparent outline-none text-sm w-full placeholder:text-[#A1A1AA]"
                placeholder="Search country…" value={q} onChange={(e) => setQ(e.target.value)}
                data-testid={`${testid}-search`}/>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {filtered.map(c => (
                <button key={c.iso2} type="button"
                  onClick={() => toggle(c.iso2)}
                  data-testid={`${testid}-opt-${c.iso2}`}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition ${
                    selectedSet.has(c.iso2)
                      ? "bg-[#D4FF00]/10 text-[#D4FF00] hover:bg-[#D4FF00]/15"
                      : "hover:bg-[#261534]"
                  }`}>
                  <span className="text-lg leading-none">{c.flag}</span>
                  <span className="flex-1 truncate text-left">{c.name}</span>
                  <span className="text-[11px] text-[#A1A1AA]">{c.dial_code}</span>
                  {selectedSet.has(c.iso2) && <Check size={14}/>}
                </button>
              ))}
            </div>
          </div>

          {selectedSet.size > 0 && (
            <div className="flex flex-wrap gap-1">
              {[...selectedSet].map((iso2) => {
                const c = countries.find(x => x.iso2 === iso2);
                if (!c) return null;
                return (
                  <span key={iso2}
                    data-testid={`${testid}-chip-${iso2}`}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#D4FF00]/10 border border-[#D4FF00]/30 text-[11px] text-[#D4FF00]">
                    <span>{c.flag}</span>
                    {c.name}
                    <button onClick={() => toggle(iso2)}
                      className="ml-1 hover:text-white">×</button>
                  </span>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------- tiny primitives -----------------------------------------------
function Label({ children, required, testid }) {
  return (
    <div data-testid={testid} className="text-[10px] font-bold uppercase tracking-widest text-[#A1A1AA] mb-1">
      {children}{required && <span className="text-[#FF007F] ml-0.5">*</span>}
    </div>
  );
}
function Input({ label, value, onChange, required, editable, testid, type = "text" }) {
  return (
    <div>
      <Label required={required}>{label}</Label>
      {editable ? (
        <input type={type} className="input-dark w-full" value={value || ""} onChange={onChange} data-testid={testid}/>
      ) : (
        <div className="input-dark opacity-80">{value || "—"}</div>
      )}
    </div>
  );
}
